<script setup>
import { ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import heroImage from '@/assets/images/Main Header.jpg';
import { useModalStore } from '@/stores/modal.js';
import { useAuthenticationStore } from '@/stores/authentication.js';
import { useGeocodeStore } from '@/stores/geocode.js';

// Stores
const modalStore = useModalStore();
const authenticationStore = useAuthenticationStore();
const geocodeStore = useGeocodeStore();

// I18n
const { t } = useI18n();

// Variables
const isLoading = ref(true);

const heroImageUrl = ref(heroImage);

// Refresh location on first load or if stale
onMounted(() => {
  const isFirstLoad = !localStorage.getItem('app_loaded');
  const savedLocation = localStorage.getItem('user_location');
  let isStale = false;
  
  if (savedLocation) {
    try {
      const location = JSON.parse(savedLocation);
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      isStale = !location.timestamp || location.timestamp < thirtyDaysAgo;
    } catch (e) {
      isStale = true;
    }
  }
  
  if (isFirstLoad || isStale) {
    geocodeStore.$reset();
    localStorage.setItem('app_loaded', 'true');
  }
});
</script>

<template>
  <div>
    <div class="real-prices-section mx-auto flex items-center justify-end px-8 lg:px-16 py-4 flex-col lg:flex-row hero-style" :style="{ backgroundImage: `url(${heroImageUrl})` }">

      <div class="w-50 lg:w-1/3 mt-8 lg:mt-0 float-right">
        <h1 class="hero-drop-shadow text-white text-6xl mb-4">{{ t('landing_page.real_prices_header') }}</h1>
        <h2 class="hero-drop-shadow text-white text-2xl font-semibold mb-2">{{ t('landing_page.real_prices_subheader') }}</h2>
        <p class="hero-drop-shadow text-white text-lg text-gray-700 mb-4">{{ t('landing_page.real_prices_text') }}</p>
        <div class="flex flex-row mt-8">
          <router-link v-if="authenticationStore.currentUser !== null" to="/request_a_quote/step/1?reset=true" class="py-2 px-8 rounded-full mb-2 md:mr-2 text-2xl font-regular free-quote">
            <span class="">
              {{ t('landing_page.free_quote') }}
            </span>
          </router-link>
          <button v-else @click="modalStore.setModal(modalStore.modalTypeConstants.LOGIN)" class="py-2 px-8 rounded-full mb-2 md:mr-2 text-2xl font-regular free-quote">{{ t('landing_page.free_quote') }}</button>
        </div>
        <!-- <div class="flex flex-row">
          <div class="flex items-center py-2 px-8 rounded-full mb-2 md:mr-2 text-2xl font-regular what-is-blitzify">
            <img src="../../assets/icons/videocam_24dp_FILL0_wght400_GRAD0_opsz24.svg" alt="Logo 1" class="text-white">
            <span class="pl-2 focus:outline-none text-lg font-semibold">
              {{ t('landing_page.what_is_blitzify', { app_name: t('app_name') }) }}
            </span>
          </div>
        </div> -->
      </div>
    </div>

    <div class="hero-video-style flex flex-wrap">
      <div class="w-100 mb-8 mt-12 grow justify-items-center">
        <h1 class="text-3xl font-semibold w-5/6">{{ t('landing_page.what_is_autowise_video_prompt') }}</h1>
      </div>

      <div class="w-100 mb-16 mt-4 grow justify-items-center">
        <video class="w-5/6" controls preload="auto" width="auto" height="auto" controlsList="nodownload" poster="https://autowise-media.s3.us-east-1.amazonaws.com/Otto2.jpg">
          <source src="https://autowise-media.s3.us-east-1.amazonaws.com/promo_2160p.mp4" type="video/mp4" media="(min-width: 2160px)">
          <source src="https://autowise-media.s3.us-east-1.amazonaws.com/promo_2160p.webm" type="video/webm" media="(min-width: 2160px)">

          <source src="https://autowise-media.s3.us-east-1.amazonaws.com/promo_1440p.mp4" type="video/mp4" media="(min-width: 1440px)">
          <source src="https://autowise-media.s3.us-east-1.amazonaws.com/promo_1440p.webm" type="video/webm" media="(min-width: 1440px)">

          <source src="https://autowise-media.s3.us-east-1.amazonaws.com/promo_1080p.mp4" type="video/mp4" media="(min-width: 1080px)">
          <source src="https://autowise-media.s3.us-east-1.amazonaws.com/promo_1080p.webm" type="video/webm" media="(min-width: 1080px)">

          <source src="https://autowise-media.s3.us-east-1.amazonaws.com/promo_720p.mp4" type="video/mp4">
          <source src="https://autowise-media.s3.us-east-1.amazonaws.com/promo_720p.webm" type="video/webm">
        </video>
      </div>
    </div>

    <div class="px-8 lg:px-16 py-8">
      <h1 class="text-4xl font-semibold mb-2">{{ t('landing_page.out_door_prices') }}</h1>
      <h2 class="text-xl font-regular mb-2">{{ t('landing_page.out_door_prices_text') }}</h2>
    </div>

    <div class="px-8 lg:px-16 pb-8 mx-auto">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="bg-white rounded-lg overflow-hidden flex flex-col xl:flex-row landing-page-card justify-evenly">
          <div class="xl:w-1/2 p-4 flex items-center">
            <img src="../../assets/images/aw1.png" alt="Image 1" class="w-auto max-h-[225px] mx-auto">
          </div>
          <div class="xl:w-1/2 py-4 px-4 lg:px-8 flex items-center">
            <div>
              <h2 class="text-xl font-bold mb-2">{{ t('landing_page.free_account_perks.title') }}</h2>
              <p class="font-semibold">{{ t('landing_page.free_account_perks.text') }}</p>
              <button class="font-semibold py-2 px-4 rounded-full mt-4 inline-block" @click="modalStore.setModal(modalStore.modalTypeConstants.SIGN_UP)">{{ t('landing_page.free_account_perks.button_text') }}</button>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg overflow-hidden flex flex-col xl:flex-row landing-page-card justify-evenly">
          <div class="xl:w-1/2 p-4 flex items-center">
            <img src="../../assets/images/awguide.png" alt="Image 1" class="w-auto max-h-[225px] mx-auto">
          </div>
          <div class="xl:w-1/2 py-4 px-4 lg:px-8 flex items-center">
            <div>
              <h2 class="text-xl font-bold mb-2">{{ t('landing_page.car_specific_accessories.title', { app_name: t('app_name') }) }}</h2>
              <p class="font-semibold">{{ t('landing_page.car_specific_accessories.text[0]', { app_name: t('app_name') }) }}</p>
              <p class="font-semibold">{{ t('landing_page.car_specific_accessories.text[1]', { app_name: t('app_name') }) }}</p>
              <a class="font-semibold py-2 px-4 rounded-full mt-4 inline-block" href="https://wiseguide.getautowise.com" target="_blank">{{ t('landing_page.wise_guide.button_text') }}</a>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg overflow-hidden flex flex-col xl:flex-row landing-page-card justify-evenly">
          <div class="xl:w-1/2 p-4 flex items-center">
            <img src="../../assets/images/otto garage.png" alt="Image 1" class="w-auto max-h-[225px] mx-auto">
          </div>
          <div class="xl:w-1/2 py-4 px-4 lg:px-8 flex items-center">
            <div>
              <h2 class="text-xl font-bold mb-2">{{ t('landing_page.understands_vehicle.title') }}</h2>
              <p class="font-semibold">{{ t('landing_page.understands_vehicle.text') }}</p>
              <a href="https://wiseguide.getautowise.com/your-garage/" target="_blank" class="font-semibold py-2 px-4 rounded-full mt-4 inline-block">{{ t('landing_page.understands_vehicle.button_text') }}</a>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg overflow-hidden flex flex-col xl:flex-row landing-page-card justify-evenly">
          <div class="xl:w-1/2 p-4 flex items-center">
            <img src="../../assets/images/aw3.png" alt="Image 1" class="w-auto max-h-[225px] mx-auto">
          </div>
          <div class="xl:w-1/2 py-4 px-4 lg:px-8 flex items-center">
            <div>
              <h2 class="text-xl font-bold mb-2">{{ t('landing_page.disrupting_auto_experience.title') }}</h2>
              <p class="font-semibold">{{ t('landing_page.disrupting_auto_experience.text') }}</p>
             <a href="https://wiseguide.getautowise.com/features/" target="_blank" class="font-semibold py-2 px-4 rounded-full mt-4 inline-block">{{ t('landing_page.disrupting_auto_experience.button_text') }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/views/landing_page/landing_page';

.hero-style {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: 35% 10%;
  height: 800px;
}
.hero-video-style {
  background: $header-light;
}

// Tailwind apparently doesn't have this built in
.hero-drop-shadow {
  text-shadow: 5px 5px 10px #000000;
}
</style>
